import { useMemo, useState } from 'react'
import { Flex } from '@mantine/core'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'

import { BudgetHeader } from '@/modules/budget/components/BudgetHeader/BudgetHeader'
import { BudgetSummary } from '@/modules/budget/components/BudgetSummary/BudgetSummary'
import { Transactions } from '@/modules/budget/components/Transactions/Transactions'

import { NoAccounts } from '@/components/@Shared/NoAccounts/NoAccounts'
import { ShadowBox } from '@/components/@Shared/ShadowBox/ShadowBox'
import { TransactionSyncNotice } from '@/components/@Shared/TransactionSyncNotice/TransactionSyncNotice'
import { Insights } from '@/components/Insights/Insights'
import { Loader } from '@/components/Loader/Loader'

import { TransactionType } from '@/constants/transactions.constants'

import { useBankingServiceBankingControllerGetAccounts } from '@/__generated__/queries'
import type { AccountData } from '@/__generated__/requests'
import { page as InsightPage } from '@/__generated__/requests'

export function Component() {
  const now = dayjs()

  const [startDate, setStartDate] = useState(now.startOf('month').toISOString())
  const [endDate, setEndDate] = useState(now.endOf('month').toISOString())
  const [transactionType, setTransactionType] = useState(
    TransactionType.SPENDING,
  )
  const { data, isFetching, isLoading } =
    useBankingServiceBankingControllerGetAccounts()

  const userAccounts = useMemo<AccountData[]>(
    () => data?.accounts ?? [],
    [data],
  )

  const handleMonthChange = (value: Dayjs) => {
    setEndDate(value.endOf('month').toISOString())
    setStartDate(value.startOf('month').toISOString())
  }

  const handleChangeTransactionType = (type: TransactionType) => {
    setTransactionType(type)
  }

  if (isLoading || isFetching) return <Loader isAuthenticatedLayout />

  if (!userAccounts.length) return <NoAccounts />

  return (
    <>
      <TransactionSyncNotice mb={24} />

      <Insights page={InsightPage.BUDGET} staticMargin />

      <ShadowBox p={0}>
        <BudgetHeader
          now={now}
          onMonthChange={handleMonthChange}
          onTransactionTypeChange={handleChangeTransactionType}
          selectedType={transactionType}
        />

        <Flex
          direction={{
            base: 'column',
            lg: 'row',
          }}
        >
          <BudgetSummary
            endDate={endDate}
            selectedType={transactionType}
            startDate={startDate}
          />

          <Transactions
            endDate={endDate}
            startDate={startDate}
            type={transactionType}
          />
        </Flex>
      </ShadowBox>
    </>
  )
}

Component.displayName = 'Budget'
